import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { fmtDateTime12 } from "@/lib/time";
import { Inbox, Mail, Phone, MapPin, Briefcase, DollarSign, Calendar } from "lucide-react";

export default function AdminLeads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/leads")
      .then((r) => {
        setRows(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-leads" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <Inbox size={16} /> CUSTOMER ENQUIRIES & LEADS
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Leads & Inquiries
          </h1>
        </div>

        <div className="text-xs text-white/50 font-semibold bg-black/60 px-4 py-2 rounded-xl border border-white/10 w-fit">
          Total Leads: <span className="text-amber-300 font-bold">{rows.length}</span>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="premium-spinner mb-3"></div>
          <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest animate-pulse">Loading Leads...</div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {rows.map((r) => {
            const typeStr = (r.type || "CONTACT").toUpperCase();
            return (
              <div
                key={r.id}
                className="rounded-2xl border border-amber-400/70 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between space-y-4 hover:scale-[1.01] transition-all"
                data-testid={`lead-${r.id}`}
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <h3 className="font-heading text-2xl font-bold text-white">{r.name}</h3>
                    <span className={`text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full border shadow-sm shrink-0 ${
                      typeStr.includes("MEMBERSHIP") ? "bg-amber-950/80 text-amber-300 border-amber-400/50" :
                      typeStr.includes("FRANCHISE") ? "bg-emerald-950/80 text-emerald-400 border-emerald-400/50" :
                      "bg-cyan-950/80 text-cyan-400 border-cyan-400/50"
                    }`}>
                      {typeStr}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-white/70">
                    <div className="flex items-center gap-2">
                      <Mail size={13} className="text-amber-400 shrink-0" />
                      <span className="truncate">{r.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={13} className="text-amber-400 shrink-0" />
                      <span>{r.phone}</span>
                    </div>
                    {r.city && (
                      <div className="flex items-center gap-2">
                        <MapPin size={13} className="text-amber-400 shrink-0" />
                        <span>{r.city}</span>
                      </div>
                    )}
                    {r.investment_budget && (
                      <div className="flex items-center gap-2 text-emerald-400 font-bold">
                        <DollarSign size={13} className="shrink-0" />
                        <span>Budget: {r.investment_budget}</span>
                      </div>
                    )}
                    {r.role && (
                      <div className="flex items-center gap-2 text-purple-300">
                        <Briefcase size={13} className="shrink-0" />
                        <span>Role: {r.role} · {r.experience}</span>
                      </div>
                    )}
                  </div>

                  {r.subject && (
                    <div className="text-xs font-bold text-amber-300 pt-1">
                      Subject: <span className="text-white font-normal">{r.subject}</span>
                    </div>
                  )}

                  {r.message && (
                    <div className="p-4 rounded-xl border border-white/10 bg-black/60 italic text-white/90 text-sm leading-relaxed">
                      "{r.message}"
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-[11px] text-white/40 pt-2 border-t border-white/10 font-semibold">
                  <Calendar size={12} className="text-amber-400" />
                  <span>{fmtDateTime12(r.created_at)}</span>
                </div>
              </div>
            );
          })}

          {rows.length === 0 && (
            <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border border-white/10 rounded-2xl font-medium">
              No leads or inquiries received yet.
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
